/**
 * @file   src\Pages\CwoSupportChat.tsx
 * @brief  Support Chat page.
 * @date   JUL, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */

import { Container, Button, Row, Col, useOutletContext, useEffect, useState, Badge, useParams, Modal, React } from '../utils/thirdpartyComponents';
import { AppContextType, TicketDetailItem } from '../interfaces/generalInterface';
import CWTextarea from '../components/CWTextarea';
import AvatarStaff from '../assets/images/avatar-staff.svg';
import AvatarCustomer from '../assets/images/avatar-customer.svg';
import { pageURLs } from '../utils/pageUrls';
import axios from '../config/axiosConfig';
import { TicketApi } from '../utils/apiUrls';
import { MessageToaster } from '../utils/toastUtil';
import { getFromLocalStorage } from '../utils/helper';
import CWSelect from '../components/CWSelect';
import SmallLoader from '../components/SmallLoader';
import SyncIcon from '../assets/images/icons/syncicon';

const CwoSupportChat = () => {

    // Access the context
    const { updateData } = useOutletContext<AppContextType>();

    const param = useParams();
    const toaster = new MessageToaster()

    const [formData, setFormData] = useState({
        message: ''
    })
    const [loading, setLoading] = useState(true);

    const options = [
        { label: 'Open', value: 1 },
        { label: 'Closed', value: 2 },
        { label: 'Re-Open', value: 3 }
    ];

    const [ticketData, setTicketData] = useState<TicketDetailItem>({
        chatList: [],
        ticketDetails: {
            editedOn: '',
            locationID: '',
            locationName: '',
            message: '',
            phone: '',
            status: 0,
            subject: '',
            ticketNumber: ''
        }
    })
    // Handle errors
    const [errors, setErrors] = useState({
        message: ''
    });

    const [chatEntered, setChatEntered] = useState(false)
    const [showChangeStatusConfirmation, setShowChangeStatusConfirmation] = useState(false)
    const [statusUpdated, setStatusUpdated] = useState(false)
    const [selectedOption, setSelectedOption] = useState(0)
    const storageData = getFromLocalStorage('CARWASH_OWNER_DATA');
    const userInfo = storageData && storageData !== null ? storageData : null;

    // To display heading
    useEffect(() => {
        handleUpdate()
        getTicketDetails()
    }, [chatEntered, statusUpdated])

    // Update the data
    const handleUpdate = () => {
        updateData({ heading: "Customer Support Desk", url: pageURLs.customer_support_desk, showBackButton: true, showMainMenu: true, activeLink: "manage-car-wash" });
    };

    // Triggers on entering input
    const handleInputChange = async (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        const { name, value } = event.target;

        // To remove validation when user enters input
        const keys = Object.keys(errors);
        if (keys.includes(name)) {
            setErrors({
                ...errors,
                [name]: ""
            })
        }

        setFormData({
            ...formData,
            [name]: value.trim(),
        });

    };

    // Get Ticket Details
    const getTicketDetails = async () => {

        // To refresh chat on entering new chat
        setChatEntered(false)

        try {
            const { data } = await axios.get(TicketApi.CARWASH_OWNER_GET_TICKET_DETAILS + param.supportId)

            if (data.error === 0) {
                const ticketDetail = data.data
                setTicketData(ticketDetail)
            }
            else {
                // show error message
                toaster.toastError(data.message);
            }
        } catch (error: any) {
            console.error('Error fetching ticket information:', error.message);
            throw new Error(error.response?.data?.message || error.message);
        } finally {
            setLoading(false)
        }
    }

    const formatLastUpdatedDate = (lastUpdatedDate: string) => {
        const isoDate = lastUpdatedDate;
        const date = new Date(isoDate);

        // Convert to desired format
        const options: Intl.DateTimeFormatOptions = {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true,
        };

        const formattedDate = date.toLocaleString('en-US', options);
        return formattedDate
    }

    // On submitting message
    const chatSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        try {
            const { data } = await axios.post(TicketApi.CARWASH_OWNER_POST_MESSAGE, {
                supportId: param.supportId ? parseInt(param.supportId) : 0,
                message: formData.message
            })

            if (data?.error === 0) {
                setChatEntered(true)
                //Clear entered message
                setFormData({ message: "" })
            }
            else {
                // show error message
                toaster.toastError(data?.message);
            }
        } catch (error: any) {
            throw new Error(error?.response?.data?.message || error?.message);
        }
    }

    // On status update show confirmation
    const changeStatus = async (selectedOption: any) => {
        if (selectedOption.value !== ticketData.ticketDetails.status) {
            setSelectedOption(selectedOption.value)
            setStatusUpdated(false)
            setShowChangeStatusConfirmation(true)
        }
    }


    const formatChatDate = (chatDate: string) => {
        const isoDate = chatDate;
        const date = new Date(isoDate);

        // Convert to desired format
        const options: Intl.DateTimeFormatOptions = {
            month: 'long',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
            hour12: true
        };

        const formattedChatDate = date.toLocaleString('en-US', options);
        return formattedChatDate
    }

    //Hide ppopup
    const handleOnHide = () => {
        setShowChangeStatusConfirmation(false)
    }

    const onStatusUpdate = async () => {
        try {
            setLoading(true)
            const { data } = await axios.post(TicketApi.CARWASH_OWNER_UPDATE_TICKET_STATUS, { supportId: param.supportId ? parseInt(param.supportId) : 0, status: selectedOption })

            if (data?.error === 0) {
                setShowChangeStatusConfirmation(false)
                // show success message
                toaster.toastSuccess(data?.message);
                setStatusUpdated(true)
            }
            else {
                // show error message
                toaster.toastError(data?.message);
            }
        } catch (error: any) {
            throw new Error(error?.response?.data?.message || error?.message);
        }
        finally {
            setLoading(false); // Hide loader
        }
    }


    return (

        <div className='support-chat-wrap'>
            <Container>
                <div className="support-status-block d-flex align-items-center justify-content-between">
                    <h6><span className='f-semi'>Ticket Number:</span>{ticketData.ticketDetails.ticketNumber}</h6>
                    <h6><span className='f-semi'>Car Wash Location:</span> {ticketData.ticketDetails.locationName}</h6>

                    {ticketData.ticketDetails.status != 2 ?
                        <div className="stat-group d-flex align-items-center">
                            <h6><span className='f-semi'>Ticket Status</span></h6>
                            <Badge className={ticketData.ticketDetails.status === 2 ? 'warning' : 'active'}>  {
                                options.find(option => option.value === ticketData.ticketDetails.status)?.label
                            }</Badge>
                        </div>
                        :
                        <CWSelect
                            id="status"
                            name="status"
                            options={options.filter(option => option.value != 1)}
                            dropdownGap={0}
                            dropdownPosition="auto"
                            values={options.filter(option => option.value === ticketData.ticketDetails.status)}
                            multi={false}
                            searchable={false}
                            onChange={(selectedOptions) => changeStatus(selectedOptions[0])}
                            label="Ticket Status"
                        />
                    }
                </div>
                <Row>
                    <Col md={8}>
                        <div className="message-history">
                            <div className="title-sec d-flex align-items-center justify-content-between">
                                <h6 className='f-bold'>Message History</h6>
                                <Button variant="outline" className="d-flex align-items-center secondary-btn btn-refresh" onClick={getTicketDetails}><SyncIcon /> Refresh Chats</Button>
                            </div>
                            <div className="message-wrap">
                                {ticketData.chatList.map((chat) => (
                                    <div className="message-block scroll-custom d-flex flex-column" key={chat.chatID}>

                                        {chat.createdBy === userInfo.userId ?
                                            <div className="message-group customer-chat d-flex">
                                                <div className="profile-pic"><img src={AvatarCustomer} alt="Car Wash App" /></div>
                                                <div className="chat-sec">
                                                    <div className="bubble customer">
                                                        {chat.message}
                                                    </div>
                                                    <p>{formatChatDate(chat.createdOn)}</p>
                                                </div>
                                            </div> :
                                            <div className="message-group d-flex">
                                                <div className="profile-pic"><img src={AvatarStaff} alt="Car Wash App" /></div>
                                                <div className="chat-sec">
                                                    <div className="bubble staff">
                                                        {chat.message}
                                                    </div>
                                                    <p>{formatChatDate(chat.createdOn)}</p>
                                                </div>
                                            </div>
                                        }

                                    </div>
                                )

                                )}
                                <div className="reply position-relative">
                                    <form onSubmit={chatSubmit}>
                                        <CWTextarea
                                            id="message"
                                            name="message"
                                            type="text"
                                            value={formData.message}
                                            placeholder="Reply"
                                            onChange={handleInputChange}
                                        />
                                        <Button title="Submit" variant="primary" className="primary-btn" type="submit">Submit</Button>
                                    </form>

                                </div>
                            </div>
                        </div>
                    </Col>
                    <Col md={4}>
                        <div className="support-details h-100">
                            <div className="info">
                                <p className='title f-semi'>Last Update Time</p>
                                <p>{formatLastUpdatedDate(ticketData.ticketDetails.editedOn)}</p>
                            </div>
                            <div className="info">
                                <p className='title f-semi'>Subject</p>
                                <p>{ticketData.ticketDetails.subject}</p>
                            </div>
                            <div className="info">
                                <p className='title f-semi'>Details</p>
                                <p>{ticketData.ticketDetails.message}</p>
                            </div>
                        </div>
                    </Col>
                </Row>
            </Container >

            <Modal
                show={showChangeStatusConfirmation}
                onHide={handleOnHide}
                centered
                className='add-modal'
            >
                <Modal.Body className='text-center'>
                    <h5 className='f-bold text-center'>Are you sure you want to change Ticket status?</h5>
                    <div className="btn-sec d-grid">
                        <Button title='Cancel' variant="light" className="secondary-btn" onClick={handleOnHide}>Cancel</Button>
                        <Button title='Yes' variant="primary" className='primary-btn btn-main m-0' onClick={onStatusUpdate}>Yes</Button>
                    </div>
                </Modal.Body>
                {loading && <SmallLoader />}
            </Modal>
        </div >
    )
}

export default CwoSupportChat;
